'use client';

import { AuthSessionTransitionHost } from '@/components/auth/auth-session-transition-host';
import { DeferredGoogleOneTap } from '@/components/auth/shell/deferred-google-one-tap';
import { ActiveThemeProvider } from '@/components/layout/system/active-theme-provider';
import { ChunkLoadRecovery } from '@/components/layout/system/chunk-load-recovery';
import { RouteTransitionBar } from '@/components/layout/system/route-transition-bar';
import { ThemeProvider } from '@/components/layout/theme/stable-theme-provider';
import { GiftRewardToastHost } from '@/components/layout/user/gift-reward-toast-host';
import { AnalyticsLazyLoader } from '@/components/providers/analytics-lazy-loader';
import { CreditsRefreshSync } from '@/components/providers/credits-refresh-sync';
import { CrispLazyLoader } from '@/components/providers/crisp-lazy-loader';
import { GoogleAnalyticsLoader } from '@/components/providers/google-analytics-loader';
import { QueryProvider } from '@/components/providers/query-provider';
import { TooltipProvider } from '@/components/ui/feedback/notification/tooltip';
import { websitePublicConfig } from '@/config/website-public';
import { AuthSessionProvider } from '@/lib/auth/auth-session-provider';
import { type ReactNode, Suspense } from 'react';

interface ProvidersProps {
  children: ReactNode;
  enableGoogleOneTap: boolean;
}

/**
 * Providers
 *
 * This component is used to wrap the app in the providers.
 *
 * - ThemeProvider: Provides the theme to the app.
 * - ActiveThemeProvider: Provides the active theme to the app.
 * - TooltipProvider: Provides the tooltip to the app.
 */
export function Providers({ children, enableGoogleOneTap }: ProvidersProps) {
  const defaultMode = websitePublicConfig.ui.mode?.defaultMode ?? 'system';

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme={defaultMode}
      forcedTheme="dark"
      enableSystem={true}
      disableTransitionOnChange
    >
      <AuthSessionProvider>
        <GoogleAnalyticsLoader />
        <Suspense fallback={null}>
          <AnalyticsLazyLoader />
        </Suspense>
        <CrispLazyLoader />
        {enableGoogleOneTap ? <DeferredGoogleOneTap /> : null}
        <ChunkLoadRecovery />
        <Suspense fallback={null}>
          <RouteTransitionBar />
        </Suspense>
        <QueryProvider>
          <AuthSessionTransitionHost />
          <CreditsRefreshSync />
          <GiftRewardToastHost />
          <ActiveThemeProvider>
            <TooltipProvider>{children}</TooltipProvider>
          </ActiveThemeProvider>
        </QueryProvider>
      </AuthSessionProvider>
    </ThemeProvider>
  );
}
